import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Play, Sparkles } from "lucide-react";
import { CALENDLY_DEMO_URL } from "@/constants/links";
import heroVideo from "@/assets/providedassets/homepagebackground vedio .mp4";

const STATS = [
  { value: "100+", label: "Trusted brands" },
  { value: "50K+", label: "Rooms visualized" },
  { value: "98%", label: "Satisfaction" },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-neutral-900">
      <video
        src={heroVideo}
        autoPlay
        muted
        loop
        playsInline
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
        aria-hidden="true"
      />
      {/* Lighter overlay on mobile so video is visible, slightly darker on desktop */}
      <div className="pointer-events-none absolute inset-0 bg-black/50 sm:bg-black/40" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" aria-hidden="true" />

      <div className="relative mx-auto flex min-h-[100svh] max-w-7xl flex-col items-center justify-center px-5 pb-16 pt-28 text-center sm:px-6 md:px-8 lg:pb-24 lg:pt-36">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="flex flex-col items-center"
        >
          <motion.div
            variants={fadeUp}
            className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-white shadow-sm ring-1 ring-white/20 backdrop-blur-sm sm:text-sm"
          >
            <Sparkles className="h-3.5 w-3.5 text-white" />
            AI-powered visualization for retail
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="mt-5 text-3xl font-bold leading-[1.15] tracking-tight text-white drop-shadow-lg sm:text-5xl xl:text-6xl"
          >
            Experience every product{" "}
            <span className="text-primary">before you buy.</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-4 max-w-xl text-base font-medium leading-relaxed text-white/90 drop-shadow sm:mt-6 sm:max-w-2xl sm:text-lg md:text-xl"
          >
            NEIV helps retailers sell more by enabling customers to visualize
            products in their own spaces using AI — photorealistic, in seconds.
          </motion.p>

          <motion.div
            variants={fadeUp}
            className="mt-7 flex w-full flex-col gap-3 sm:mt-9 sm:w-auto sm:flex-row sm:gap-4"
          >
            <a
              href={CALENDLY_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-semibold text-neutral-900 shadow-lg transition-all duration-300 hover:bg-primary hover:text-white"
            >
              Book demo
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 bg-white/10 px-8 py-3.5 text-base font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/20"
            >
              <Play className="h-4 w-4" />
              Try our visualizer
            </Link>
          </motion.div>

          <motion.div
            variants={fadeUp}
            className="mt-10 flex flex-row items-center gap-0 divide-x divide-white/30 sm:mt-12"
          >
            {STATS.map((stat) => (
              <div key={stat.label} className="px-5 text-center first:pl-0 last:pr-0 sm:px-8">
                <p className="text-lg font-bold text-white drop-shadow sm:text-2xl">{stat.value}</p>
                <p className="mt-0.5 text-[10px] text-white/70 sm:text-sm">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
